import { type Key, useCallback } from "react"

import {
  useGetApplicationToolInvocations,
  useGetInventoryApplication,
  useGetInventoryBranch,
  useGetInventoryCodeRepoPackages,
  useGetInventoryImage,
  useGetInventoryVirtualMachine,
  useGetPackageDescribe,
  useGetPackageVulnerabilities
} from "@/api/useInventory"
import { useIssuesByResourceTriage } from "@/api/useTriage"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import {
  Params,
  predicateHelpers,
  type QueryClient,
  useDelete,
  useLoadMore,
  useQueryClient,
  useUpdate
} from "@/hooks/useHttp"
import useServices from "@/hooks/useServices"
import { useFeatureFlags } from "@/hooks/useUser"
import { overrideDemoIssueResponse } from "@/organisms/Drawers/components/TriageDrawer/components/DemoOverrides/demoOverrides"
import { enrichDemoIssuesResponse } from "@/organisms/Drawers/components/TriageDrawer/components/MitigationDemoCards/enrich"
import { getCountParams } from "@/organisms/Drawers/hooks/useCounts"
import {
  getApplicationTabData,
  getToolInvocationsData
} from "@/pages/DiscoveryPage/application/application-inventory/useApplicationInventory.selector"
import { getBranchTabData } from "@/pages/DiscoveryPage/application/branch-inventory/useBranchInventory.selector"
import { getIssuesTriageSelector } from "@/pages/TriagePage/application/useTriage.selector"
import { getInitialDataFromQueryClient } from "../common/common"
import {
  getDescribe,
  getImageTabData,
  getPackagesData,
  getPackageVulnerabilities
} from "./useInventory.selectors"

import type {
  InventoryCodeRepoPackagesParams,
  ToolDecision
} from "@/api/useInventory.types"
import type {
  PackageCommonProps,
  Project,
  PropsUseEntity,
  VirtualMachineConfig,
  VirtualMachineModule
} from "./useInventory.types"

export const getData = (res: { data: object | unknown[] }) =>
  Array.isArray(res.data) ? res.data : [res.data]

export const useEntity = <S, E extends { id: string } = { id: string }>(
  id: string | undefined,
  { resourceRequestURL, hook, select: selectFn }: PropsUseEntity<S>
) => {
  const params = { needle: `/${id}`, pageNumber: "0" }
  const queryClient = useQueryClient()
  const context = hook(params, {
    select: (res: any) => {
      const nextRes = { ...res, data: getData(res) }

      if (typeof selectFn === "function") return selectFn?.(nextRes)

      return nextRes
    },
    enabled: !!id,
    initialData: () =>
      getInitialDataFromQueryClient(
        queryClient.getQueriesData<KosmosApi.Response<E[]> | undefined>({
          queryKey: [resourceRequestURL]
        }),
        (item) => item.id === id
      )
  })

  return context
}

export const useBranchEntity = (id?: string) => {
  const context = useEntity<typeof getBranchTabData>(id, {
    resourceRequestURL: RESOURCES_NAMES.INVENTORIES.BRANCH,
    hook: useGetInventoryBranch,
    select: getBranchTabData
  })
  const repositoryName = context.response?.data[0]?.repositoryName || ""
  const codeRepoId = context.response?.data[0]?.codeRepoId || ""
  const sourceControl = context.response?.data[0]?.sourceControl || ""
  const packageManagers: PackageManager[] =
    context.response?.data[0]?.packageManagers || []
  const estimatedPackageManagers: PackageManager[] =
    context.response?.data[0]?.estimatedPackageManagers || []

  const isPackageManagerEstimated =
    packageManagers?.filter((manager) =>
      estimatedPackageManagers.includes(manager)
    ).length > 0

  const relatedImages: string[] =
    context.response?.data?.[0]?.relatedImages?.map(
      (i: { imageId: string }) => i.imageId.split("@")[0]
    ) || []

  return [
    context,
    {
      repositoryName,
      codeRepoId,
      sourceControl,
      isPackageManagerEstimated,
      relatedImages: [...new Set(relatedImages)] as string[]
    }
  ] as const
}

export const useImageEntity = (id: string | undefined) => {
  const context = useEntity<typeof getImageTabData>(id, {
    resourceRequestURL: RESOURCES_NAMES.INVENTORIES.IMAGES,
    hook: useGetInventoryImage,
    select: getImageTabData
  })
  const imageId = context.response?.data[0]?.imageId

  return [context, imageId] as const
}

export const useApplicationEntity = (id: string | undefined) => {
  const context = useEntity(id, {
    resourceRequestURL: RESOURCES_NAMES.INVENTORIES.APPLICATION,
    hook: useGetInventoryApplication,
    select: getApplicationTabData
  })
  const resourceId = context.response?.data[0]?.data?.resourceIds?.[0]

  return [context, resourceId] as const
}

export const useApplicationToolInvocations = (
  id: string | undefined,
  decision?: ToolDecision
) =>
  useGetApplicationToolInvocations(
    id,
    { decision },
    { select: getToolInvocationsData }
  )

export const usePackagesCodeRepository = (
  id: string | undefined,
  params: InventoryCodeRepoPackagesParams
) => {
  const [entityContext, { repositoryName }] = useBranchEntity(id)
  const config = { enabled: !!repositoryName }
  const context = useGetInventoryCodeRepoPackages(repositoryName, params, {
    select: getPackagesData,
    ...config
  })
  const countsContext = useGetInventoryCodeRepoPackages(
    repositoryName,
    getCountParams(),
    config
  )

  return [entityContext.isLoading ? entityContext : context, countsContext]
}

export const useGetIssuesForResource = (
  resourceIdName: string | undefined,
  filterParams: Record<string, unknown>
) => {
  const { packageId, projectId } = filterParams
  const isPackage =
    packageId && projectId && resourceIdName === `${projectId}-${packageId}`
  const componentParams = isPackage
    ? filterParams
    : { resourceId: resourceIdName, ...filterParams }
  const isInternalTenant = !!useFeatureFlags("isInternalTenant")
  const isDemo = useServices().auth.isDemo()
  const select = useCallback(
    (response: Parameters<typeof getIssuesTriageSelector>[0]) =>
      getIssuesTriageSelector(
        overrideDemoIssueResponse(
          enrichDemoIssuesResponse(response, isInternalTenant),
          isDemo
        )
      ),
    [isInternalTenant, isDemo]
  )
  const context = useIssuesByResourceTriage(componentParams, {
    enabled: !!resourceIdName,
    select
  })

  const commonCountParams = {
    issueType: filterParams.issueType as string,
    issueStatus: "open",
    ...getCountParams()
  }
  const countParams = isPackage
    ? { packageId, projectId, ...commonCountParams }
    : { resourceId: resourceIdName, ...commonCountParams }

  const countsContext = useIssuesByResourceTriage(countParams, {
    enabled: !!resourceIdName,
    select: getIssuesTriageSelector
  })
  return [context, countsContext]
}

export const usePackageVulnerabilities = (props: PackageCommonProps) => {
  const context = useGetPackageVulnerabilities(props, {
    enabled: !!props.projectId && !!props.id,
    select: getPackageVulnerabilities
  })

  return context
}

export const usePackageDescribe = (props: PackageCommonProps) => {
  const context = useGetPackageDescribe(props, {
    enabled: !!props.projectId && !!props.id,
    select: getDescribe
  })

  return context
}

export const useVirtualMachineEntity = (id: string | undefined) => {
  const vmInstancesEnabledFF = useFeatureFlags("vmInstancesEnabled")
  const context = useGetInventoryVirtualMachine(
    { needle: `/${id}` },
    { enabled: !!vmInstancesEnabledFF && !!id }
  )
  const entity = Array.isArray(context.response?.data)
    ? context.response.data[0]
    : context.response?.data
  const resourceId = entity?.id
  const instanceId = entity?.data?.instanceId

  return [context, resourceId, instanceId] as const
}

export const useVirtualMachineModules = (id: string | undefined) => {
  const vmInstancesEnabledFF = useFeatureFlags("vmInstancesEnabled")
  const context = useGetInventoryVirtualMachine<VirtualMachineModule>(
    { needle: `/${id}/modules` },
    { enabled: !!vmInstancesEnabledFF && !!id }
  )

  return context
}

const optionsRepositoryConfig = (queryClient: QueryClient) => ({
  onSuccess: () =>
    queryClient.invalidateQueries({
      predicate: predicateHelpers.includesKeysPredicate([
        RESOURCES_NAMES.INVENTORIES.BRANCH
      ])
    })
})

export const useVirtualMachineConfig = (id: string | undefined) => {
  const vmInstancesEnabledFF = useFeatureFlags("vmInstancesEnabled")
  const config = { enabled: !!vmInstancesEnabledFF && !!id }
  const [context, _, instanceId] = useVirtualMachineEntity(id)
  const autoScalingGroup =
    (
      (Array.isArray(context.response?.data)
        ? context.response.data
        : [context.response?.data])?.[0]?.data.autoScalingGroups || []
    ).filter(Boolean)?.[0] || ""

  const contextCfg = useGetInventoryVirtualMachine<VirtualMachineConfig>(
    { needle: `/${id}/config` },
    config
  )
  const contextModules = useGetInventoryVirtualMachine<VirtualMachineModule>(
    { needle: `/${id}/modules` },
    config
  )

  const deleteContext = useDelete(
    `${RESOURCES_NAMES.INVENTORIES.VM}`,
    undefined,
    undefined,
    optionsRepositoryConfig
  )
  const updateContext = useUpdate(
    `${RESOURCES_NAMES.INVENTORIES.VM}`,
    undefined,
    undefined,
    optionsRepositoryConfig
  )

  const isPending =
    context.isPending || contextCfg.isPending || contextModules.isPending

  const updateConfig = useCallback(
    async (
      values: {
        correlateToProject: string
        pathSelected: string[]
        project: { data: { codeRepoId: string; paths: string[] } }
      },
      pathSelectedByRoot: Key[] | false | undefined
    ) => {
      try {
        await updateContext.mutateAsync({
          needle: `/${id}/config`,
          data: {
            code_repo_id: values.project.data.codeRepoId,
            vm_paths: pathSelectedByRoot ? [] : values.pathSelected,
            code_repo_paths: values.project.data.paths
          }
        })
        await contextCfg.refetch()
      } catch (err: unknown) {
        return Promise.reject(err)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateContext.mutateAsync, id]
  )

  const deleteConfig = useCallback(
    async (vm_paths?: string[]) => {
      try {
        await deleteContext.mutateAsync({
          needle: `${id}/config`,
          data: vm_paths ? { vm_paths } : undefined
        })
        // stale paths are listed with the modules only while correlated
        await Promise.all([contextCfg.refetch(), contextModules.refetch()])
      } catch (err: unknown) {
        return Promise.reject(err)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteContext.mutateAsync, id]
  )

  return {
    isPending,
    isUpdating: isPending || updateContext.isPending,
    instanceId,
    autoScalingGroup,
    deleteConfig,
    updateConfig,
    cfg: contextCfg.response?.data,
    modules: contextModules.response?.data,
    isVmStale: !!contextModules.response?.stale
  }
}

export const useProjects = (params: Params<{ projectName?: string }> = {}) => {
  const context = useLoadMore<Project>(
    `${RESOURCES_NAMES.INVENTORIES.BRANCH}/projects`,
    params,
    { initialPageParam: 0 }
  )

  return context
}

export const useUpdateVM = () => {
  const context = useUpdate(`${RESOURCES_NAMES.INVENTORIES.VM}`)

  return context
}
